import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from './firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import './AdminContacts.css';

function AdminContacts() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [contacts, setContacts] = useState([]);
  const [uniqueRoles, setUniqueRoles] = useState([]);
  
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: '', role: '', phone: '', aadhaar: '', address: ''
  });

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "verified_contacts"));
      const fetchedData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      // Sort alphabetically by name
      fetchedData.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setContacts(fetchedData);

      // Extract unique roles for the Autocomplete datalist
      const roles = [...new Set(fetchedData.map(c => c.role.trim().toLowerCase()))];
      setUniqueRoles(roles.sort());

    } catch (err) {
      alert("Error fetching contacts: " + err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Normalize role string to prevent slight case typos
      const normalizedRole = form.role.trim().toLowerCase();
      
      const payload = {
        name: form.name.trim(),
        role: normalizedRole,
        phone: form.phone.trim(),
        aadhaar: form.aadhaar.trim(),
        address: form.address.trim(),
        updatedAt: serverTimestamp()
      };

      if (editingId) {
        await updateDoc(doc(db, "verified_contacts", editingId), payload);
        alert("✅ Contact updated!");
      } else {
        payload.createdAt = serverTimestamp();
        await addDoc(collection(db, "verified_contacts"), payload);
        alert("✅ New contact added!");
      }
      
      handleCancel();
      fetchContacts();
    } catch (err) {
      alert("Error saving contact: " + err.message);
    }
    setSaving(false);
  };

  const handleEdit = (contact) => {
    setEditingId(contact.id);
    setForm({
      name: contact.name || '',
      role: contact.role || '',
      phone: contact.phone || '',
      aadhaar: contact.aadhaar || '',
      address: contact.address || ''
    });
    window.scrollTo(0,0);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to permanently delete this contact?")) {
      try {
        await deleteDoc(doc(db, "verified_contacts", id));
        fetchContacts();
      } catch (err) {
        alert("Error deleting: " + err.message);
      }
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm({ name: '', role: '', phone: '', aadhaar: '', address: '' });
  };

  if (loading) return <div style={{padding:'50px', textAlign:'center'}}>Loading Verified Contacts...</div>;

  // Group contacts by their Role for UI display
  const groupedContacts = contacts.reduce((acc, curr) => {
    const r = curr.role || 'unassigned';
    if (!acc[r]) acc[r] = [];
    acc[r].push(curr);
    return acc;
  }, {});

  return (
    <div className="contacts-wrapper">
      
      {/* HEADER */}
      <header className="ledger-header" style={{position: 'sticky', top: 0, zIndex: 100, background: 'white', borderBottom: '1px solid #eee', padding: '15px 0'}}>
        <div style={{maxWidth: '1200px', margin: '0 auto', padding: '0 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <div>
            <h2 style={{margin: 0, color: '#2c3e50'}}>👷‍♂️ Verified Society Contacts</h2>
            <p style={{margin: '5px 0 0 0', fontSize: '13px', color: '#7f8c8d'}}>Manage trusted plumbers, electricians, guards, and staff.</p>
          </div>
          <button onClick={() => navigate('/admin')} style={{padding: '8px 16px', background: '#34495e', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'}}>
            ← Back to Admin
          </button>
        </div>
      </header>

      <div style={{maxWidth: '1200px', margin: '0 auto', padding: '0 20px'}} className="contacts-layout fade-in-up">
        
        {/* --- LEFT COLUMN: THE FORM --- */}
        <div>
          <div className="form-card floating-card" style={{position: 'sticky', top: '100px'}}>
            <h3 style={{margin: '0 0 20px 0', color: '#3498db', borderBottom: '2px solid #eee', paddingBottom: '10px'}}>
              {editingId ? "✏️ Edit Contact" : "➕ Add New Contact"}
            </h3>
            
            <form onSubmit={handleSubmit}>
              
              {/* HTML5 Datalist for Autocomplete Job Roles */}
              <label style={{fontSize:'12px', fontWeight:'bold', color:'#666'}}>Job Role / Category <span style={{color:'red'}}>*</span></label>
              <input 
                className="input-field" 
                list="job-roles" 
                placeholder="e.g. Plumber, Security Guard" 
                value={form.role} 
                onChange={e => setForm({...form, role: e.target.value})} 
                required 
              />
              <datalist id="job-roles">
                {uniqueRoles.map(r => <option key={r} value={r} />)}
              </datalist>

              <label style={{fontSize:'12px', fontWeight:'bold', color:'#666'}}>Full Name <span style={{color:'red'}}>*</span></label>
              <input className="input-field" placeholder="Person's Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />

              <div className="row-inputs">
                <div style={{flex: 1}}>
                  <label style={{fontSize:'12px', fontWeight:'bold', color:'#666'}}>Phone Number <span style={{color:'red'}}>*</span></label>
                  <input className="input-field" type="tel" placeholder="Mobile" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required />
                </div>
                <div style={{flex: 1}}>
                  <label style={{fontSize:'12px', fontWeight:'bold', color:'#666'}}>Aadhaar Number</label>
                  <input className="input-field" placeholder="ID Proof (Optional)" value={form.aadhaar} onChange={e => setForm({...form, aadhaar: e.target.value})} />
                </div>
              </div>

              <label style={{fontSize:'12px', fontWeight:'bold', color:'#666'}}>Residential Address</label>
              <textarea className="input-field" rows="2" placeholder="Where do they live?" value={form.address} onChange={e => setForm({...form, address: e.target.value})} />

              <div style={{display: 'flex', gap: '15px', marginTop: '10px'}}>
                {editingId && (
                  <button type="button" onClick={handleCancel} className="btn-primary-full" style={{background: '#e0e0e0', color: '#333'}}>
                    Cancel
                  </button>
                )}
                <button type="submit" disabled={saving} className="btn-primary-full" style={{background: '#3498db'}}>
                  {saving ? "Saving..." : (editingId ? "Update Contact" : "Save Contact")}
                </button>
              </div>

            </form>
          </div>
        </div>

        {/* --- RIGHT COLUMN: THE LIST --- */}
        <div>
          {Object.keys(groupedContacts).length === 0 ? (
            <div style={{padding: '40px', textAlign: 'center', background: 'white', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.04)'}}>
              <h3 style={{color: '#7f8c8d'}}>No contacts added yet.</h3>
              <p style={{fontSize: '13px', color: '#999'}}>Use the form to add your first society contact.</p>
            </div>
          ) : (
            Object.keys(groupedContacts).sort().map(role => (
              <div key={role} className="role-group fade-in-up">
                <div className="role-header">
                  <span>{role}</span>
                  <span className="role-count">{groupedContacts[role].length} Person(s)</span>
                </div>
                
                <div>
                  {groupedContacts[role].map(contact => (
                    <div key={contact.id} className="contact-card">
                      <div className="contact-info">
                        <h4>{contact.name}</h4>
                        <div className="contact-details">
                          <span><strong>📞 Phone:</strong> {contact.phone}</span>
                          {contact.aadhaar && <span><strong>🆔 Aadhaar:</strong> {contact.aadhaar}</span>}
                          {contact.address && <span><strong>🏠 Address:</strong> {contact.address}</span>}
                        </div>
                      </div>
                      
                      <div style={{display: 'flex', gap: '10px', flexDirection: 'column'}}>
                        <button onClick={() => handleEdit(contact)} style={{padding: '5px 15px', background: '#e3f2fd', color: '#1976d2', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px'}}>Edit</button>
                        <button onClick={() => handleDelete(contact.id)} style={{padding: '5px 15px', background: '#ffebee', color: '#c0392b', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px'}}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

      </div>
    </div>
  );
}

export default AdminContacts;